import {Component, EventEmitter, Inject, Input, OnInit, Output} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {DateUtil} from '../../../../common/util/DateUtil';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {forkJoin} from 'rxjs';
import {finalize, take} from 'rxjs/operators';
import {saveAs} from 'file-saver';
import {LookupService} from '../../../../common/services/lookup.service';
import {IssueTicketDTO} from '../../../models/IssueTicketDTO';
import {LookupValue} from '../../../types/lookup-value.enum';
import {IssueTicketDetailsLinkDTO} from '../../../models/IssueTicketDetailsLinkDTO';
import {IssueTicketAttachmentsLinkDTO} from '../../../models/IssueTicketAttachmentsLinkDTO';
import {KeycloakService} from 'keycloak-angular';
import {MatSnackBar} from '@angular/material/snack-bar';
import moment from 'moment';
import {FileDTO} from '../../../models/FileDTO';
import {FileUploadService} from '../../../../common/services/file-upload.service';
import {IssueTicketAttachmentsDTO} from '../../../models/IssueTicketAttachmentsDTO';
import {IssueTicketCaseLinkDTO} from '../../../models/IssueTicketCaseLinkDTO';
import {IssueTicketDetailsDTO} from '../../../models/IssueTicketDetailsDTO';
import {SharedFormService} from '../../../../common/services/shared-form-service';
import {NameValueCodeDTO} from '../../../../common/models/NameValueCodeDTO';


enum Controls {
  Narrative = 'narrative',
}

enum RouteHeadings {
  ADD = 'Add Issue',
  EDIT = 'Edit Issue',
}

@Component({
  selector: 'app-add-issue-ticket-dialog',
  templateUrl: './add-issue-ticket-dialog.component.html',
  styleUrls: [
    '../../../../../../styles/global/_dialog-styles.scss',
    './add-issue-ticket-dialog.component.scss'],
})
export class AddIssueTicketDialogComponent implements OnInit {
  readonly controls = Controls;
  readonly dateFormat = DateUtil.dateFormat;
  readonly lookupValue = LookupValue;
  readonly MAX_ATTACHMENT: number = 10;

  @Input() isEditMode: boolean;
  @Input() issueTicketCase: IssueTicketDTO;
  @Input() formGroup: FormGroup;

  @Output() newIssueTicketCase: EventEmitter<IssueTicketDTO> = new EventEmitter<IssueTicketDTO>();

  heading: string;
  addCommentsFormGroup: FormGroup;
  fileUploadConfig: any = {};
  nameFormControl: FormControl;
  startDateFormControl: FormControl;
  endDateFormControl: FormControl;
  dueDateFormControl: FormControl;
  reporterFormControl: FormControl;
  assigneeFormControl: FormControl;
  statusFormControl: FormControl;
  resolutionFormControl: FormControl;
  priorityFormControl: FormControl;
  moduleFormControl: FormControl;
  issueTicketDetailsLinkFormControl: FormControl; // Comments list
  issueTicketAttachmentsLinkFormControl: FormControl; // Attachments list

  usersLookUpValues: NameValueCodeDTO[];
  statusLookUpValues: NameValueCodeDTO[];
  resolutionLookUpValues: NameValueCodeDTO[];
  priorityLookUpValues: NameValueCodeDTO[];
  moduleLookUpValues: NameValueCodeDTO[];

  issueTicketAttachmentsList: IssueTicketAttachmentsLinkDTO[] = [];
  issueTicketDetailsList: IssueTicketDetailsLinkDTO[] = [];
  isResolutionRequired: boolean;

  private loggedInUserName: string;

  constructor(
    private formBuilder: FormBuilder,
    private lookupService: LookupService,
    private fileUploadService: FileUploadService,
    private keycloakService: KeycloakService,
    private snackBar: MatSnackBar,
    private sharedFormService: SharedFormService,
    public dialogRef: MatDialogRef<AddIssueTicketDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: any) {

    this.isEditMode = this.data.isEditMode;
    this.issueTicketCase = this.data.issueTicketCase;
    this.formGroup = this.data.formGroup;
  }

  get narrativeFormControlValue() {
    return this.addCommentsFormGroup.get(this.controls.Narrative).value || null;
  }

  ngOnInit(): void {
    this.initIssuesStepFormControls();
    this.initUploadFileConfig();
    this.initSubscriptions();

    this.keycloakService
      .loadUserProfile()
      .then(profile => {
        this.loggedInUserName = `${profile.firstName} ${profile.lastName}`;
      });

    if (this.isEditMode) {
      this.setIssueCaseFormForEdit(this.issueTicketCase);
      this.getFileAttachmentsDetails();
    }
    else {
      this.heading = 'Add Issue';
    }
  }

  initSubscriptions(): void {
    forkJoin([
      this.lookupService.getListOfLookupValuesWithCode(this.lookupValue.User),
      this.lookupService.getListOfLookupValuesWithCode(this.lookupValue.IssueStatus),
      this.lookupService.getListOfLookupValuesWithCode(this.lookupValue.Resolution),
      this.lookupService.getListOfLookupValuesWithCode(this.lookupValue.Priority),
      this.lookupService.getListOfLookupValuesWithCode(this.lookupValue.Module)
    ])
      .pipe(
        take(1),
      )
      .subscribe({
        next: ([users, statuses, resolutions, priorities, modules]) => {
          if (users) {
            this.usersLookUpValues = users;
            this.reporterFormControl.setValue(this.loggedInUserName);
            this.assigneeFormControl.setValue(this.loggedInUserName);
          }
          if (statuses) {
            this.statusLookUpValues = statuses;

            const isOpenExists = this.statusLookUpValues.find(item => item.name === 'Open');
            if (isOpenExists) {
              this.statusFormControl.setValue('Open');
              this.clearValidator('resolution'); // Resolution: only required when Status = Closed;
            }
          }
          if (resolutions) {
            this.resolutionLookUpValues = resolutions;
          }
          if (priorities) {
            this.priorityLookUpValues = priorities;
            this.priorityFormControl.setValue('Medium');
          }
          if (modules) {
            this.moduleLookUpValues = modules;
          }
        },
        error: (error) => {
          console.log(error);
        }
      });
  }

  initUploadFileConfig(): void {
    // Note: MAX LIMIT FILE 10 ATTACHMENTS can be processed
    for (let configIndex = 0; configIndex < this.MAX_ATTACHMENT; configIndex++) {
      this.fileUploadConfig = {
        ...this.fileUploadConfig,
        [`file${configIndex}`]: {name: `file${configIndex}`, uploadedFileData: null}
      };

      // Note: Add extra object in case of ADD new attachment.
      this.fileUploadConfig = {
        ...this.fileUploadConfig,
        newAttachment: {name: 'newAttachment', uploadedFileData: null}
      };
    }
  }

  initIssuesStepFormControls(): void {
    this.nameFormControl = new FormControl(null, [Validators.required]);
    this.startDateFormControl = new FormControl(new Date());
    this.endDateFormControl = new FormControl();
    this.dueDateFormControl = new FormControl();
    this.reporterFormControl = new FormControl(null, [Validators.required]);
    this.assigneeFormControl = new FormControl(null, [Validators.required]);
    this.statusFormControl = new FormControl(null, [Validators.required]);
    this.resolutionFormControl = new FormControl(null, [Validators.required]);
    this.priorityFormControl = new FormControl(null, [Validators.required]);
    this.moduleFormControl = new FormControl(null);
    this.issueTicketDetailsLinkFormControl = new FormControl([]);
    this.issueTicketAttachmentsLinkFormControl = new FormControl([]);

    this.formGroup = this.formBuilder.group({
      name: this.nameFormControl,
      startDate: this.startDateFormControl,
      endDate: this.endDateFormControl,
      dueDate: this.dueDateFormControl,
      reporter: this.reporterFormControl,
      assignee: this.assigneeFormControl,
      status: this.statusFormControl,
      resolution: this.resolutionFormControl,
      priority: this.priorityFormControl,
      module: this.moduleFormControl,
      issueTicketDetailsLink: this.issueTicketDetailsLinkFormControl,
      issueTicketAttachmentsLink: this.issueTicketAttachmentsLinkFormControl
    });

    // FormGroup to handle New Comments on Add Case
    this.addCommentsFormGroup = this.formBuilder.group({
      narrative: this.formBuilder.control(null),
    });
  }

  onDownloadFile($event: MouseEvent, configName: string): void {
    $event.preventDefault();

    if (!this.fileUploadConfig[configName].uploadedFileData) {
      console.log('No file exists');
      return;
    }
    const {name, extension, file} = this.fileUploadConfig[configName].uploadedFileData;
    const blobData = this.fileUploadService.base64toBlob(file, 'data:application/octet-stream;base64');
    const blob = new Blob([blobData]);
    saveAs(blob, `${name}${extension}`);
  }

  onDeleteIssueTicketAttachment($event: MouseEvent, issueTicketAttachment: IssueTicketAttachmentsLinkDTO): void {
    $event.stopPropagation();

    const {fileId} = issueTicketAttachment && issueTicketAttachment.issueTicketAttachment;
    this.fileUploadService.deleteFile(fileId)
      .pipe(
        take(1),
        finalize(() => {
          // Note: In Case of Success or Error both case local state is cleared to remove attachment:
          this.issueTicketAttachmentsList = this.filterAttachments(issueTicketAttachment);
          this.onUpdateConfig();
        })
      )
      .subscribe(
        () => {
          // Notes:
          //  Case Add: File can be deleted on "New" attachment added:
          //  Case Edit: If Reference file fails to delete remove attachment from local state
        },
        (errorResponse) => {
          console.log(`Attachment deleted from Angular local state not from server!`);
          console.log('See Note:', errorResponse);
          // Case Edit: can't delete the file because it’s already a foreign key reference in a table:
        });
  }

  onDeleteIssueTicketComment($event: MouseEvent, issueTicketDetail: IssueTicketDetailsLinkDTO): void {
    $event.stopPropagation();
    const {narrative, issueTicketDetailsId} = issueTicketDetail && issueTicketDetail.issueTicketDetail;

    this.issueTicketDetailsList = this.issueTicketDetailsList
      .filter((issueTicketDetailsLinkDTO: IssueTicketDetailsLinkDTO) => {
        const eachIssueTicketDetailsId = issueTicketDetailsLinkDTO.issueTicketDetail?.issueTicketDetailsId;
        const eachNarrative = issueTicketDetailsLinkDTO.issueTicketDetail?.narrative;

        if (eachIssueTicketDetailsId === null) {
          return eachNarrative.toLowerCase().trim() !== narrative.toLowerCase().trim();
        }
        return eachIssueTicketDetailsId !== issueTicketDetailsId && eachNarrative !== narrative;
      })
      .map((issueTicket: IssueTicketDetailsLinkDTO) => {
        issueTicket.updated = new Date();
        issueTicket.updatedBy = this.loggedInUserName;
        return issueTicket;
      });
  }

  onSaveIssueTicket(): void {
    if (this.isEditMode) {
      this.dialogRef.close({
        formGroup: this.formGroup,
        issueTicketCasePayload: this.getIssueTicketCasePayload(), // Updated Payload
        isEditMode: true
      });
    }
    else {
      // Add "new" Issue Ticket
      this.dialogRef.close({
        issueTicketCasePayload: this.getIssueTicketCasePayload(),
        isEditMode: false
      });
    }
  }

  onFileUploadSuccess(uploadedFileResponse: any): void {
    if (!uploadedFileResponse) {
      return;
    }
    const {configName} = uploadedFileResponse;
    // Add Case:
    // - Attach file -> Change file
    this.setUploadedFileData(configName, uploadedFileResponse);
    this.onAddNewAttachment(configName);
    this.getFileAttachmentsDetails();
    this.clearUploadedFileData(configName);
  }

  onAddNewComment(): void {
    const value = this.narrativeFormControlValue;
    if (!value) {
      this.openSnackBar('Add note description', 'Dismiss');
      return;
    }
    const newIssueDetail = this.issueTicketDetailPayload();
    this.issueTicketDetailsList = [...this.issueTicketDetailsList, newIssueDetail];
    this.issueTicketDetailsLinkFormControl.patchValue(this.issueTicketDetailsList);
    this.resetAddCommentFormGroup();
  }

  onAddNewAttachment(configName: string): void {
    const issueTicketAttachment = this.issueTicketAttachmentPayload(configName);
    if (!issueTicketAttachment.issueTicketAttachment.fileId) {
      return;
    }
    this.issueTicketAttachmentsList = [...this.issueTicketAttachmentsList, issueTicketAttachment];
    this.issueTicketAttachmentsLinkFormControl.patchValue(this.issueTicketAttachmentsList);
  }

  onInputChangedUpdateIssueName(value: string): void {
    if (this.isEditMode) {
      this.heading = `${RouteHeadings.EDIT} - ${value}`;
    }
    else {
      this.heading = `${RouteHeadings.ADD} - ${value}`;
    }
  }

  onSelectionChanged(value: string, label: string): void {
    if (label === 'Status') {
      // Resolution: only required when Status = Closed
      if (value === 'Closed') {
        this.setValidator('resolution');
        this.isResolutionRequired = true;
      }
      else {
        this.clearValidator('resolution');
        this.isResolutionRequired = false;
      }
    }
  }

  onBlurComment(): void {
    const value = this.narrativeFormControlValue;
    if (!value) {
      this.addCommentsFormGroup.get(this.controls.Narrative).reset();
    }
  }

  getFileName(idx: number): string {
    const file = this.fileUploadConfig['file' + idx];
    const fileName = file?.uploadedFileData?.name || '';
    const extension = file?.uploadedFileData?.extension || '';
    return `${fileName}${extension}`;
  }

  private onUpdateConfig(): void {
    const attachmentConfigs = [];
    for (let index = 0; index < this.issueTicketAttachmentsList.length; index++) {
      const configName = this.getAttachmentConfigName(this.issueTicketAttachmentsList[index]);
      const newConfig = {
        [`file${index}`]: {...this.fileUploadConfig[configName], name: `file${index}`}
      };
      attachmentConfigs.push(newConfig);
    }

    // Reset Config:
    this.initUploadFileConfig();

    // Reassign Config Uploaded Attachments at start
    // Attachments are displayed `file0`...`file9`
    for (let index = 0; index < attachmentConfigs.length; index++) {
      this.fileUploadConfig = {
        ...this.fileUploadConfig,
        ...attachmentConfigs[index]
      };
    }
  }

  private filterAttachments(issueTicketAttachment: IssueTicketAttachmentsLinkDTO): IssueTicketAttachmentsLinkDTO[] {
    const existingAttachmentsList = [...this.issueTicketAttachmentsList];
    const {issueTicketAttachmentsId, fileId} = issueTicketAttachment && issueTicketAttachment.issueTicketAttachment;

    const index = existingAttachmentsList
      .findIndex((item: IssueTicketAttachmentsLinkDTO) => {
        if (issueTicketAttachmentsId) {
          return item.issueTicketAttachment.issueTicketAttachmentsId === issueTicketAttachmentsId;
        }
        else {
          return (
            item.issueTicketAttachment.issueTicketAttachmentsId === issueTicketAttachmentsId &&
            item.issueTicketAttachment.fileId === fileId
          );
        }
      });
    if (index > -1) {
      existingAttachmentsList.splice(index, 1);
    }
    else {
      this.openSnackBar('No Matching row found to delete attachment', 'Dismiss');
    }
    return existingAttachmentsList;
  }

  private getAttachmentConfigName(issueTicketAttachment: IssueTicketAttachmentsLinkDTO): string {
    const configName = Object.keys(this.fileUploadConfig)
      .find((key: string) => {
        const config: FileDTO = this.fileUploadConfig[key].uploadedFileData;
        if (config) {
          return issueTicketAttachment.issueTicketAttachment.fileId === config.fileId;
        }
      });
    if (configName) {
      return configName;
    }
    return null;
  }

  private resetAddCommentFormGroup(): void {
    this.addCommentsFormGroup.reset();
  }

  private getIssueTicketCasePayload(): IssueTicketCaseLinkDTO {
    const newIssueTicketCaseLinkDTO: IssueTicketCaseLinkDTO = new IssueTicketCaseLinkDTO();
    const isStatusClosed = this.statusFormControl.value === 'Closed';

    newIssueTicketCaseLinkDTO.created = this.isEditMode ? this.issueTicketCase.created : new Date();
    newIssueTicketCaseLinkDTO.createdBy = this.isEditMode ? this.issueTicketCase.createdBy : this.loggedInUserName;
    newIssueTicketCaseLinkDTO.updated = this.isEditMode ? new Date() : null;
    newIssueTicketCaseLinkDTO.updatedBy = this.isEditMode ? this.loggedInUserName : null;
    newIssueTicketCaseLinkDTO.tenantId = null;
    newIssueTicketCaseLinkDTO.issueTicketDTO = new IssueTicketDTO();
    newIssueTicketCaseLinkDTO.issueTicketDTO.name = this.nameFormControl.value;
    newIssueTicketCaseLinkDTO.issueTicketDTO.issueTicketId = this.isEditMode ? this.issueTicketCase.issueTicketId : null;
    newIssueTicketCaseLinkDTO.issueTicketDTO.startDate = this.startDateFormControl.value ? moment(this.startDateFormControl.value).toDate().getTime() : null;

    // Completion Date: Field is populated in code with the date that the status first changed to Closed
    newIssueTicketCaseLinkDTO.issueTicketDTO.endDate = isStatusClosed ? moment(new Date()).toDate().getTime() : null;

    newIssueTicketCaseLinkDTO.issueTicketDTO.dueDate = this.dueDateFormControl.value ? moment(this.dueDateFormControl.value).toDate().getTime() : null;
    newIssueTicketCaseLinkDTO.issueTicketDTO.reporter = this.usersLookUpValues.find(item => item.name === this.reporterFormControl.value);
    newIssueTicketCaseLinkDTO.issueTicketDTO.assignee = this.usersLookUpValues.find(item => item.name === this.assigneeFormControl.value);
    newIssueTicketCaseLinkDTO.issueTicketDTO.status = this.statusLookUpValues.find(item => item.name === this.statusFormControl.value);
    newIssueTicketCaseLinkDTO.issueTicketDTO.resolution = this.resolutionLookUpValues.find(item => item.name === this.resolutionFormControl.value);
    newIssueTicketCaseLinkDTO.issueTicketDTO.priority = this.priorityLookUpValues.find(item => item.name === this.priorityFormControl.value);
    newIssueTicketCaseLinkDTO.issueTicketDTO.module = this.moduleLookUpValues.find(item => item.name === this.moduleFormControl.value);
    newIssueTicketCaseLinkDTO.issueTicketDTO.issueTicketDetailsLink = this.issueTicketDetailsList;
    newIssueTicketCaseLinkDTO.issueTicketDTO.issueTicketAttachmentsLink = this.issueTicketAttachmentsList;
    newIssueTicketCaseLinkDTO.issueTicketDTO.created = this.isEditMode ? this.issueTicketCase.created : new Date();
    newIssueTicketCaseLinkDTO.issueTicketDTO.createdBy = this.isEditMode ? this.issueTicketCase.createdBy : this.loggedInUserName;
    newIssueTicketCaseLinkDTO.issueTicketDTO.updated = this.isEditMode ? new Date() : null;
    newIssueTicketCaseLinkDTO.issueTicketDTO.updatedBy = this.isEditMode ? this.loggedInUserName : null;
    newIssueTicketCaseLinkDTO.issueTicketDTO.tenantId = null;
    return newIssueTicketCaseLinkDTO;
  }

  private issueTicketAttachmentPayload(configName: string): IssueTicketAttachmentsLinkDTO {
    let fileId;
    if (this.fileUploadConfig[configName].uploadedFileData) {
      fileId = this.fileUploadConfig[configName].uploadedFileData.fileId;
    }
    const issueTicketAttachment = new IssueTicketAttachmentsLinkDTO();
    issueTicketAttachment.created = new Date();
    issueTicketAttachment.createdBy = this.loggedInUserName;
    issueTicketAttachment.updated = null;
    issueTicketAttachment.updatedBy = null;
    issueTicketAttachment.tenantId = null;

    issueTicketAttachment.issueTicketAttachment = new IssueTicketAttachmentsDTO();
    issueTicketAttachment.issueTicketAttachment.issueTicketAttachmentsId = null;
    issueTicketAttachment.issueTicketAttachment.fileId = fileId ? fileId : null;
    issueTicketAttachment.issueTicketAttachment.created = new Date();
    issueTicketAttachment.issueTicketAttachment.createdBy = this.loggedInUserName;
    issueTicketAttachment.issueTicketAttachment.updated = null;
    issueTicketAttachment.issueTicketAttachment.updatedBy = null;
    issueTicketAttachment.issueTicketAttachment.tenantId = null;
    return issueTicketAttachment;
  }

  private issueTicketDetailPayload(): IssueTicketDetailsLinkDTO {
    const issueTicketDetails = new IssueTicketDetailsLinkDTO();
    issueTicketDetails.issueTicketDetail = new IssueTicketDetailsDTO();
    issueTicketDetails.created = new Date();
    issueTicketDetails.createdBy = this.loggedInUserName;
    issueTicketDetails.updated = null;
    issueTicketDetails.updatedBy = null;
    issueTicketDetails.tenantId = null;
    issueTicketDetails.issueTicketDetail.issueTicketDetailsId = null;
    issueTicketDetails.issueTicketDetail.narrative = this.addCommentsFormGroup.controls[this.controls.Narrative].value || null;
    issueTicketDetails.issueTicketDetail.narrativeDate = new Date().getTime();
    return issueTicketDetails;
  }

  private getFileAttachmentsDetails(): void {
    const observables = [];
    if (this.issueTicketAttachmentsList && this.issueTicketAttachmentsList.length > 0) {
      this.issueTicketAttachmentsList.forEach((attachment: IssueTicketAttachmentsLinkDTO) => {
        observables.push(this.fileUploadService.getFile(attachment.issueTicketAttachment.fileId));
      });
    }
    else {
      return;
    }
    if (observables.length > 0) {
      forkJoin(observables)
        .pipe(
          take(1),
        )
        .subscribe((responses: FileDTO[]) => {
            if (responses && responses.length > 0) {
              responses.forEach((fileResponse: FileDTO, index: number) => {
                this.fileUploadConfig[`file${index}`].uploadedFileData = fileResponse;
              });
            }
          },
          error => console.log(error));
    }
  }

  private openSnackBar(message: string, action: string): void {
    this.snackBar.open(message, action, {
      duration: 10000
    });
  }

  private setUploadedFileData(configName: string, file: any): void {
    this.fileUploadConfig[configName].uploadedFileData = file;
  }

  private clearUploadedFileData(configName: string): void {
    this.fileUploadConfig[configName].uploadedFileData = null;
  }

  private setIssueCaseFormForEdit(data: IssueTicketDTO): void {
    const {
      name,
      startDate,
      endDate,
      dueDate,
      reporter,
      assignee,
      status,
      resolution,
      priority,
      module,
      issueTicketDetailsLink,
      issueTicketAttachmentsLink
    } = data;

    this.nameFormControl.patchValue(name);
    if (startDate) {
      this.startDateFormControl.patchValue(new Date(startDate));
    }
    if (endDate) {
      this.endDateFormControl.patchValue(new Date(endDate));
    }
    if (dueDate) {
      this.dueDateFormControl.patchValue(new Date(dueDate));
    }
    this.reporterFormControl.patchValue(reporter ? reporter.name : null);
    this.assigneeFormControl.patchValue(assignee ? assignee.name : null);
    this.statusFormControl.patchValue(status ? status.name : null);
    this.resolutionFormControl.patchValue(resolution ? resolution.name : null);
    this.priorityFormControl.patchValue(priority ? priority.name : null);
    this.moduleFormControl.patchValue(module ? module.name : null);
    this.issueTicketDetailsLinkFormControl.patchValue(issueTicketDetailsLink ? issueTicketDetailsLink : []);
    this.issueTicketDetailsList = issueTicketDetailsLink ? issueTicketDetailsLink : [];
    this.issueTicketAttachmentsLinkFormControl.patchValue(issueTicketAttachmentsLink ? issueTicketAttachmentsLink : []);
    this.issueTicketAttachmentsList = issueTicketAttachmentsLink ? issueTicketAttachmentsLink : [];

    this.onInputChangedUpdateIssueName(name);
  }

  private setValidator(controlName: string): void {
    this.sharedFormService.setValidator(this.formGroup, controlName, [Validators.required]);
  }

  private clearValidator(controlName: string): void {
    this.sharedFormService.clearValidator(this.formGroup, controlName);
  }
}

